/**
 * Evaluation budgets (docs/SHEETS.md 4.1, mirroring backend/calc/core/limits.py 5.8).
 *
 * A sheet runs in the reader's own browser, so a runaway evaluation costs them a
 * locked tab rather than costing us a CPU. That makes these limits an ergonomics
 * feature more than a security one -- but the hard caps still matter, because
 * "no hang, no silent wrong answer" (SHEETS.md 3) is a promise about the reader's
 * machine as much as ours.
 *
 * Enforcement lives on the main thread, not in the worker: a worker stuck in a
 * tight SymPy loop cannot time itself out. The client terminates it.
 */
export const LIMITS = {
  /** Log-and-continue. The cell shows a spinner past this. */
  cellSoftMs: 500,
  /** Terminate the worker. The cell reports a timeout error. */
  cellHardMs: 5_000,
  /** Whole-sheet budget for one reactive pass. */
  sheetHardMs: 30_000,

  /** Refuse beyond this rather than let a document become unusable. */
  maxCells: 200,
  /** Serialized document size, matching the API's 256 KB body cap (15.4). */
  maxDocumentBytes: 256 * 1024,

  /** Expression nesting depth, to bound parse and evaluation cost. */
  maxExpressionDepth: 32,
  /** Total elements in a matrix literal. */
  maxMatrixElements: 400,
  /** Samples a plot cell may request. */
  maxPlotSteps: 500,

  /** Startup budget for Pyodide. Past this the editor says so rather than hanging. */
  workerBootMs: 60_000,
} as const

export type LimitKey = keyof typeof LIMITS

/** i18n keys for the message shown when each limit is hit. */
export const LIMIT_MESSAGE_KEYS: Partial<Record<LimitKey, string>> = {
  cellHardMs: 'sheets.error.cell_timeout',
  sheetHardMs: 'sheets.error.sheet_timeout',
  maxCells: 'sheets.error.too_many_cells',
  maxDocumentBytes: 'sheets.error.document_too_large',
  maxExpressionDepth: 'sheets.error.expression_too_deep',
  maxMatrixElements: 'sheets.error.matrix_too_large',
  maxPlotSteps: 'sheets.error.too_many_steps',
}
